/**
 * Export/Import dialog for engine interchange (_docs/tilemap/06). Export
 * offers 'joystick' (the .jtileset/.jtilemap + manifest zip for
 * JoystickEngine) and 'json' (canonical slice for Unity/Godot/custom
 * pipelines); both are queued jobs polled with the SAME `useJobPolling` hook
 * the graph editor's ImportExportDialog already uses. Import accepts an
 * engine zip, a bare .jtileset/.jtilemap zip, or a Tiled .tmx/.tsx zip — the
 * server auto-detects which — and shows the plan/report once it completes.
 */

import { useCallback, useState } from 'react';
import { tilemapClient, type ExportJob, type ImportJob } from '../api/tilemapClient';
import { useJobPolling } from '../../graph/panels/useJobPolling';
import { catalogStore } from '../store/catalogStore';

export function TilemapExportImportDialog({
    projectUuid,
    tilemapUuid,
    tilemapName,
    onClose,
}: {
    projectUuid: string;
    tilemapUuid: string;
    tilemapName: string;
    onClose: () => void;
}) {
    const [tab, setTab] = useState<'export' | 'import'>('export');
    const [format, setFormat] = useState<'joystick' | 'json'>('joystick');
    const [exportJob, setExportJob] = useState<ExportJob | null>(null);
    const [importJob, setImportJob] = useState<ImportJob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useJobPolling(exportJob, useCallback(() => tilemapClient.getExportJob(exportJob!.uuid), [exportJob]), setExportJob);
    useJobPolling(importJob, useCallback(() => tilemapClient.getImportJob(importJob!.uuid), [importJob]), (job) => {
        setImportJob(job);
        if (job.status === 'completed') void catalogStore.getState().refresh(projectUuid);
    });

    const startExport = async () => {
        setBusy(true);
        setError(null);
        try {
            setExportJob(await tilemapClient.exportTilemap(projectUuid, tilemapUuid, format));
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setError(null);
        try {
            setImportJob(await tilemapClient.importTilemapZip(projectUuid, file));
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="modal modal-blur show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Export / Import — {tilemapName}</h5>
                        <button className="btn-close" onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        <div className="d-flex border-bottom mb-3">
                            <button className={`btn btn-sm flex-fill rounded-0 ${tab === 'export' ? 'btn-light' : 'btn-white'}`} onClick={() => setTab('export')}>Export</button>
                            <button className={`btn btn-sm flex-fill rounded-0 ${tab === 'import' ? 'btn-light' : 'btn-white'}`} onClick={() => setTab('import')}>Import</button>
                        </div>

                        {error && <div className="alert alert-danger py-2">{error}</div>}

                        {tab === 'export' && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label small">Format</label>
                                    <select className="form-select form-select-sm" value={format} onChange={(e) => setFormat(e.target.value as 'joystick' | 'json')}>
                                        <option value="joystick">JoystickEngine (.jtileset/.jtilemap zip)</option>
                                        <option value="json">Canonical JSON (Unity/Godot/custom)</option>
                                    </select>
                                </div>
                                <button className="btn btn-sm btn-primary" disabled={busy || (!!exportJob && exportJob.status !== 'completed' && exportJob.status !== 'failed')} onClick={startExport}>
                                    {busy ? 'Starting…' : 'Export'}
                                </button>

                                {exportJob && (
                                    <div className="mt-3">
                                        <div className="small text-secondary">Status: {exportJob.status}</div>
                                        {exportJob.status === 'completed' && (
                                            <a className="btn btn-sm btn-success mt-2" href={`/api/exports/${exportJob.uuid}/download`} target="_blank" rel="noreferrer">
                                                <i className="ti ti-download me-1" /> Download
                                            </a>
                                        )}
                                        {exportJob.warnings && exportJob.warnings.length > 0 && (
                                            <ul className="small text-warning mt-2 mb-0">
                                                {exportJob.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                            </ul>
                                        )}
                                        {exportJob.status === 'failed' && <div className="text-danger small mt-2">{exportJob.error}</div>}
                                    </div>
                                )}
                            </>
                        )}

                        {tab === 'import' && (
                            <>
                                <p className="small text-secondary">
                                    Upload a JoystickEngine export zip, bare .jtileset/.jtilemap files, or a Tiled
                                    (.tmx/.tsx) zip. If it references this same map, it updates in place; otherwise a new map is created.
                                </p>
                                <input type="file" accept=".zip" className="form-control form-control-sm" onChange={onFileChosen} disabled={busy} />

                                {importJob && (
                                    <div className="mt-3">
                                        <div className="small text-secondary">Status: {importJob.status}</div>
                                        {importJob.status === 'completed' && importJob.report?.plan && (
                                            <div className="small mt-2">
                                                <div><strong>{importJob.report.plan.mode === 'update' ? 'Updated' : 'Created'}</strong>: {importJob.report.plan.tilemap_name}</div>
                                                <ul className="mb-0">
                                                    {importJob.report.plan.layers.map((l, i) => (
                                                        <li key={i}>{l.name} — {l.chunk_count} chunk(s){l.matches_existing_layer ? ' (replaced)' : ' (new)'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {importJob.report?.warnings && importJob.report.warnings.length > 0 && (
                                            <ul className="small text-warning mt-2 mb-0">
                                                {importJob.report.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                            </ul>
                                        )}
                                        {importJob.status === 'failed' && <div className="text-danger small mt-2">{importJob.error}</div>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
